// frontend/src/services/dynamicPagesApi.js
// Dynamic Configurable Pages API Service — HotWifi v1.0

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const apiClient = axios.create({
  baseURL: `${API_URL}/dynamic-pages`,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  withCredentials: true,
  timeout: 30000,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const selectedTenantStr = localStorage.getItem('selectedTenant');
  const selectedBranchStr = localStorage.getItem('selectedBranch');

  let currentTenantId = null;
  if (selectedTenantStr) {
    try {
      const selectedTenant = JSON.parse(selectedTenantStr);
      currentTenantId = selectedTenant?._id || selectedTenant?.id || null;
    } catch (error) {
      console.error('[dynamicPagesAPI] Failed to parse selectedTenant from localStorage', error);
    }
  }

  if (selectedBranchStr) {
    try {
      const selectedBranch = JSON.parse(selectedBranchStr);
      const branchId = selectedBranch?._id || selectedBranch?.id;

      if (branchId) {
        let addBranchHeader = true;
        if (currentTenantId && selectedBranch?.tenant) {
          const branchTenantId = selectedBranch.tenant?._id || selectedBranch.tenant;
          if (branchTenantId && String(branchTenantId) !== String(currentTenantId)) {
            addBranchHeader = false;
          }
        }

        if (addBranchHeader) {
          config.headers['X-Branch-ID'] = branchId;
        }
      }
    } catch (error) {
      console.error('[dynamicPagesAPI] Failed to parse selectedBranch from localStorage', error);
    }
  }

  if (currentTenantId) {
    config.headers['X-Tenant-Id'] = currentTenantId;
  }

  return config;
});

apiClient.interceptors.response.use(
  (res) => res.data,
  (err) => {
    // Log full error for debugging
    if (err.response) {
      console.error('[dynamicPagesAPI] Error response:', err.response.status, JSON.stringify(err.response.data));
    }
    const validationErrors = err.response?.data?.errors;
    const message =
      err.response?.data?.message ||
      err.response?.data?.error ||
      (validationErrors?.length > 0
        ? validationErrors.map((e) => `${e.field}: ${e.message}`).join('; ')
        : null) ||
      err.message ||
      'Request failed';
    return Promise.reject(new Error(message));
  }
);

// ─── Pages ───────────────────────────────────────────────────────────────────

export const dynamicPagesAPI = {
  // GET /branches/:branchId/pages
  getPages: (branchId, params = {}) =>
    apiClient.get(`/branches/${branchId}/pages`, { params }),

  // POST /branches/:branchId/pages
  createPage: (branchId, data) =>
    apiClient.post(`/branches/${branchId}/pages`, data),

  // PATCH /branches/:branchId/pages/:id
  updatePage: (branchId, pageId, data) =>
    apiClient.patch(`/branches/${branchId}/pages/${pageId}`, data),

  // DELETE /branches/:branchId/pages/:id
  deletePage: (branchId, pageId) =>
    apiClient.delete(`/branches/${branchId}/pages/${pageId}`),

  // POST /branches/:branchId/pages/:id/toggle
  togglePage: (branchId, pageId, isActive) =>
    apiClient.post(`/branches/${branchId}/pages/${pageId}/toggle`, { isActive }),

  // ─── Module Mappings ──────────────────────────────────────────────────────

  // GET /branches/:branchId/module-mappings
  getMappings: (branchId) =>
    apiClient.get(`/branches/${branchId}/module-mappings`),

  // PUT /branches/:branchId/module-mappings
  updateMappings: (branchId, mappings) =>
    apiClient.put(`/branches/${branchId}/module-mappings`, { mappings }),

  // PUT /branches/:branchId/module-mappings/:moduleId
  updateSingleMapping: (branchId, moduleId, targetType, pageId) =>
    apiClient.put(`/branches/${branchId}/module-mappings/${moduleId}`, {
      targetType,
      pageId: pageId || undefined,
    }),

  // ─── Slots ────────────────────────────────────────────────────────────────

  // GET /branches/:branchId/pages/:pageKey/slots
  getSlots: (branchId, pageKey, params = {}) =>
    apiClient.get(`/branches/${branchId}/pages/${pageKey}/slots`, { params }),

  // POST /branches/:branchId/pages/:pageKey/slots
  createSlot: (branchId, pageKey, data) =>
    apiClient.post(`/branches/${branchId}/pages/${pageKey}/slots`, data),

  // PATCH /branches/:branchId/pages/:pageKey/slots/:slotId
  updateSlot: (branchId, pageKey, slotId, data) =>
    apiClient.patch(`/branches/${branchId}/pages/${pageKey}/slots/${slotId}`, data),

  // DELETE /branches/:branchId/pages/:pageKey/slots/:slotId
  deleteSlot: (branchId, pageKey, slotId) =>
    apiClient.delete(`/branches/${branchId}/pages/${pageKey}/slots/${slotId}`),

  // POST /branches/:branchId/pages/:pageKey/slots/:slotId/check-in
  checkIn: (branchId, pageKey, slotId, guestData) =>
    apiClient.post(
      `/branches/${branchId}/pages/${pageKey}/slots/${slotId}/check-in`,
      guestData
    ),

  // POST /branches/:branchId/pages/:pageKey/slots/:slotId/check-out
  checkOut: (branchId, pageKey, slotId) =>
    apiClient.post(
      `/branches/${branchId}/pages/${pageKey}/slots/${slotId}/check-out`
    ),

  // GET /branches/:branchId/pages/:pageKey/slots/:slotId/wifi
  getWiFiCredentials: (branchId, pageKey, slotId) =>
    apiClient.get(`/branches/${branchId}/pages/${pageKey}/slots/${slotId}/wifi`),

  // POST /branches/:branchId/pages/:pageKey/slots/:slotId/reconcile-password
  reconcilePassword: (branchId, pageKey, slotId) =>
    apiClient.post(`/branches/${branchId}/pages/${pageKey}/slots/${slotId}/reconcile-password`),

  // POST /branches/:branchId/pages/:pageKey/slots/:slotId/send-wifi-sms
  sendWifiPasswordSms: (branchId, pageKey, slotId, payload = {}) =>
    apiClient.post(`/branches/${branchId}/pages/${pageKey}/slots/${slotId}/send-wifi-sms`, payload),

  // PATCH /branches/:branchId/pages/:pageKey/slots/batch
  batchUpdate: (branchId, pageKey, updates) =>
    apiClient.patch(`/branches/${branchId}/pages/${pageKey}/slots/batch`, {
      updates,
    }),

  // GET /branches/:branchId/pages/:pageKey/slots/export
  exportSlots: (branchId, pageKey, params = {}) =>
    apiClient.get(`/branches/${branchId}/pages/${pageKey}/slots/export`, {
      params,
    }),
};

export default dynamicPagesAPI;
